import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Nav from "react-bootstrap/Nav";
import Spinner from "react-bootstrap/Spinner";
import { ToastContainer, toast } from "react-toastify";
import { NavLink, useLocation } from "react-router-dom";
import Select from "react-select";
import config from "../../Config";
import TimeTableMenu from "../TimeTable/TimeTableMenu";
import SessionsDropDown from "../Shared/SessionsDropDown";
import WingsDropDown from "../Shared/WingsDropDown";
import ClassesDropDown from "../Shared/ClassesDropDown";
import SectionDropDown from "../Shared/SectionDropDown";
import SubjectDropDown from "../Shared/SubjectDropDown";
import { scrollToTop } from "../Shared/ScrollOnTop";

function TimeTableManagement() {
  const location = useLocation();
  const [AccessKey] = useState(localStorage.getItem("AccessKey"));
  const [UserID] = useState(parseInt(localStorage.getItem("ID"), 10) || 0);
  const [MemberType] = useState(localStorage.getItem("MemberType") || "Admin");
  const [UserName] = useState(localStorage.getItem("UserName") || "");

  const [tab, setTab] = useState("masters");
  const [loading, setLoading] = useState(false);

  const [SessionId, setSessionId] = useState(localStorage.getItem("DefaultSession") || "0");
  const [WingId, setWingId] = useState(0);
  const [ClassId, setClassId] = useState(0);
  const [SectionId, setSectionId] = useState(0);

  const [weekDays, setWeekDays] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [timeTables, setTimeTables] = useState([]);
  const [teacherOptions, setTeacherOptions] = useState([]);

  const [dayName, setDayName] = useState("");
  const [dayOrder, setDayOrder] = useState(0);
  const [editingDayId, setEditingDayId] = useState(0);
  const [periodName, setPeriodName] = useState("");
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [periodOrder, setPeriodOrder] = useState(0);
  const [periodBreak, setPeriodBreak] = useState(false);
  const [periodDayId, setPeriodDayId] = useState(0);
  const [editingPeriodId, setEditingPeriodId] = useState(0);

  const [mapSubjectId, setMapSubjectId] = useState(0);
  const [mapTeacherId, setMapTeacherId] = useState(0);
  const [mapPeriods, setMapPeriods] = useState(0);
  const [mapRemarks, setMapRemarks] = useState("");
  const [editingMappingId, setEditingMappingId] = useState(0);

  const [ttName, setTtName] = useState("");
  const [ttFrom, setTtFrom] = useState("");
  const [ttTo, setTtTo] = useState("");
  const [ttRemarks, setTtRemarks] = useState("");
  const [selectedTimeTableId, setSelectedTimeTableId] = useState(0);
  const [isEditingHeader, setIsEditingHeader] = useState(false);
  const [grid, setGrid] = useState({});

  const auth = useMemo(
    () => ({
      AccessKey,
      UserID,
      MemberType,
    }),
    [AccessKey, MemberType, UserID]
  );

  useEffect(() => {
    document.title = "Time Table Management";
    loadWeekDays();
    loadTeachers();
  }, []);

  useEffect(() => {
    const q = new URLSearchParams(location.search || "");
    const requested = (q.get("tab") || "").toLowerCase();
    const qSession = parseInt(q.get("sessionId"), 10);
    const qWing = parseInt(q.get("wingId"), 10);
    const qClass = parseInt(q.get("classId"), 10);
    const qSection = parseInt(q.get("sectionId"), 10);
    if (requested === "masters" || requested === "mapping" || requested === "timetable") {
      setTab(requested);
    }
    if (qSession > 0) setSessionId(String(qSession));
    if (qWing >= 0) setWingId(qWing || 0);
    if (qClass > 0) setClassId(qClass);
    if (qSection > 0) setSectionId(qSection);
  }, [location.search]);

  useEffect(() => {
    loadPeriods();
  }, [WingId]);

  useEffect(() => {
    if (SessionId && WingId > 0 && ClassId > 0 && SectionId > 0) {
      loadMappings();
      loadTimeTables();
    } else {
      setMappings([]);
      setTimeTables([]);
    }
  }, [SessionId, WingId, ClassId, SectionId]);

  useEffect(() => {
    if (selectedTimeTableId > 0) {
      loadTimeTableDetails(selectedTimeTableId);
    } else {
      buildEmptyGrid();
    }
  }, [selectedTimeTableId, weekDays, periods, mappings]);

  const toApiDateTime = (hhmm) => {
    if (!hhmm) return null;
    return `2000-01-01T${hhmm}:00`;
  };

  const mappingKey = (subjectId, teacherId) => `${subjectId || 0}_${teacherId || 0}`;
  const isPeriodApplicableToDay = (period, dayId) => !period?.Day_ID || period.Day_ID === dayId;

  const buildEmptyGrid = () => {
    const next = {};
    weekDays.forEach((d) => {
      periods.forEach((p) => {
        next[`${d.ID}_${p.ID}`] = { mode: "", mapping: "" };
      });
    });
    setGrid(next);
  };

  const loadWeekDays = () => {
    axios
      .post(config.base_url + "TimeTableSetup/GetWeekDays", { ...auth })
      .then((res) => setWeekDays(res.data.ListofRecords || []))
      .catch(() => toast.error("Failed to load week days."));
  };

  const loadPeriods = () => {
    axios
      .post(config.base_url + "TimeTableSetup/GetPeriods", {
        ...auth,
        SchoolID: 1,
        Wing_Id: parseInt(WingId, 10) || 0,
      })
      .then((res) => setPeriods(res.data.ListofRecords || []))
      .catch(() => toast.error("Failed to load periods."));
  };

  const loadTeachers = () => {
    axios
      .post(config.base_url + "Payroll/SearchEmployees", {
        ...auth,
        Is_Resigned: false,
        SchoolID: 1,
        PageNo: 1,
        PageSize: 10000,
      })
      .then((res) => {
        const list = res.data.ListofRecords || [];
        const options = list.map((item) => {
          const designation =
            item?.Selected_Designation?.Name ||
            item?.Designation_Name ||
            item?.Designation ||
            "";
          const label = designation
            ? `${item.Name} (${item.Code}) - ${designation}`
            : `${item.Name} (${item.Code})`;
          return {
            value: item.ID,
            label,
          };
        });
        setTeacherOptions(options);
      })
      .catch(() => toast.error("Failed to load teachers."));
  };

  const loadMappings = () => {
    axios
      .post(config.base_url + "TimeTableSetup/GetClassSubjectTeacherMappings", {
        ...auth,
        SessionID: parseInt(SessionId, 10) || 0,
        SchoolID: 1,
        Wing_Id: parseInt(WingId, 10) || 0,
        Class_Id: parseInt(ClassId, 10) || 0,
        Section_Id: parseInt(SectionId, 10) || 0,
      })
      .then((res) => setMappings(res.data.ListofRecords || []))
      .catch(() => toast.error("Failed to load mappings."));
  };

  const loadTimeTables = () => {
    axios
      .post(config.base_url + "TimeTable/GetTimeTables", {
        ...auth,
        SessionID: parseInt(SessionId, 10) || 0,
        SchoolID: 1,
        Wing_Id: parseInt(WingId, 10) || 0,
        Class_Id: parseInt(ClassId, 10) || 0,
        Section_Id: parseInt(SectionId, 10) || 0,
      })
      .then((res) => setTimeTables(res.data.ListofRecords || []))
      .catch(() => toast.error("Failed to load timetables."));
  };

  const loadTimeTableDetails = (id) => {
    axios
      .post(config.base_url + "TimeTable/GetTimeTableDetails", {
        ...auth,
        TimeTable_ID: id,
      })
      .then((res) => {
        const list = res.data.ListofRecords || [];
        const next = {};
        weekDays.forEach((d) => {
          periods.forEach((p) => {
            next[`${d.ID}_${p.ID}`] = { mode: "", mapping: "" };
          });
        });
        list.forEach((row) => {
          const key = `${row.Day_ID}_${row.Period_ID}`;
          if (row.Is_Break) next[key] = { mode: "break", mapping: "" };
          else if (row.Is_FreePeriod) next[key] = { mode: "free", mapping: "" };
          else next[key] = { mode: "mapping", mapping: mappingKey(row.Subject_ID, row.Teacher_ID) };
        });
        setGrid(next);
      })
      .catch(() => toast.error("Failed to load timetable details."));
  };

  const addWeekDay = () => {
    if (!dayName.trim() || parseInt(dayOrder, 10) <= 0) {
      toast.error("Enter day name and display order.");
      return;
    }
    axios
      .post(config.base_url + "TimeTableSetup/AddWeekDay", {
        ...auth,
        Name: dayName.trim(),
        DisplayOrder: parseInt(dayOrder, 10),
      })
      .then((res) => {
        if (res.data.status_code === 1) {
          toast.success("Week day added.");
          setDayName("");
          setDayOrder(0);
          loadWeekDays();
        } else toast.error(res.data.status_message || "Failed.");
      })
      .catch(() => toast.error("Network error."));
  };

  const startEditWeekDay = (day) => {
    setEditingDayId(day.ID || 0);
    setDayName(day.Name || "");
    setDayOrder(day.DisplayOrder || 0);
  };

  const resetWeekDayForm = () => {
    setEditingDayId(0);
    setDayName("");
    setDayOrder(0);
  };

  const updateWeekDay = () => {
    if (editingDayId <= 0) {
      toast.error("Select a week day to edit.");
      return;
    }
    if (!dayName.trim() || parseInt(dayOrder, 10) <= 0) {
      toast.error("Enter day name and display order.");
      return;
    }
    axios
      .post(config.base_url + "TimeTableSetup/UpdateWeekDay", {
        ...auth,
        ID: editingDayId,
        Name: dayName.trim(),
        DisplayOrder: parseInt(dayOrder, 10),
      })
      .then((res) => {
        if (res.data.status_code === 1) {
          toast.success("Week day updated.");
          resetWeekDayForm();
          loadWeekDays();
        } else toast.error(res.data.status_message || "Failed.");
      })
      .catch(() => toast.error("Network error."));
  };

  const deleteWeekDay = (dayId) => {
    if (dayId <= 0) return;
    if (!window.confirm("Delete this week day?")) return;
    axios
      .post(config.base_url + "TimeTableSetup/DeleteWeekDay", {
        ...auth,
        ID: dayId,
      })
      .then((res) => {
        if (res.data.status_code === 1) {
          toast.success("Week day deleted.");
          if (editingDayId === dayId) {
            resetWeekDayForm();
          }
          loadWeekDays();
        } else toast.error(res.data.status_message || "Failed.");
      })
      .catch(() => toast.error("Network error."));
  };

  const addPeriod = () => {
    if (!periodName.trim() || !periodStart || !periodEnd || parseInt(periodOrder, 10) <= 0) {
      toast.error("Enter period name, start/end and order.");
      return;
    }
    axios
      .post(config.base_url + "TimeTableSetup/AddPeriod", {
        ...auth,
        SchoolID: 1,
        Wing_Id: parseInt(WingId, 10) || 0,
        Name: periodName.trim(),
        Day_ID: parseInt(periodDayId, 10) || 0,
        Period_StartTime: toApiDateTime(periodStart),
        Period_EndTime: toApiDateTime(periodEnd),
        DisplayOrder: parseInt(periodOrder, 10),
        Is_Break: periodBreak,
      })
      .then((res) => {
        if (res.data.status_code === 1) {
          toast.success("Period added.");
          setPeriodName("");
          setPeriodStart("");
          setPeriodEnd("");
          setPeriodOrder(0);
          setPeriodBreak(false);
          setPeriodDayId(0);
          loadPeriods();
        } else toast.error(res.data.status_message || "Failed.");
      })
      .catch(() => toast.error("Network error."));
  };

  const startEditPeriod = (period) => {
    setEditingPeriodId(period.ID || 0);
    setPeriodName(period.Name || "");
    setPeriodStart((period.StartTime || "").slice(0, 5));
    setPeriodEnd((period.EndTime || "").slice(0, 5));
    setPeriodOrder(period.DisplayOrder || 0);
    setPeriodBreak(!!period.Is_Break);
    setPeriodDayId(period.Day_ID || 0);
  };

  const resetPeriodForm = () => {
    setEditingPeriodId(0);
    setPeriodName("");
    setPeriodStart("");
    setPeriodEnd("");
    setPeriodOrder(0);
    setPeriodBreak(false);
    setPeriodDayId(0);
  };

  const updatePeriod = () => {
    if (editingPeriodId <= 0) {
      toast.error("Select a period to edit.");
      return;
    }
    if (!periodName.trim() || !periodStart || !periodEnd || parseInt(periodOrder, 10) <= 0) {
      toast.error("Enter period name, start/end and order.");
      return;
    }
    axios
      .post(config.base_url + "TimeTableSetup/UpdatePeriod", {
        ...auth,
        ID: editingPeriodId,
        SchoolID: 1,
        Wing_Id: parseInt(WingId, 10) || 0,
        Name: periodName.trim(),
        Day_ID: parseInt(periodDayId, 10) || 0,
        Period_StartTime: toApiDateTime(periodStart),
        Period_EndTime: toApiDateTime(periodEnd),
        DisplayOrder: parseInt(periodOrder, 10),
        Is_Break: periodBreak,
      })
      .then((res) => {
        if (res.data.status_code === 1) {
          toast.success("Period updated.");
          resetPeriodForm();
          loadPeriods();
        } else toast.error(res.data.status_message || "Failed.");
      })
      .catch(() => toast.error("Network error."));
  };

  const deletePeriod = (periodId) => {
    if (periodId <= 0) return;
    if (!window.confirm("Delete this period?")) return;
    axios
      .post(config.base_url + "TimeTableSetup/DeletePeriod", {
        ...auth,
        ID: periodId,
      })
      .then((res) => {
        if (res.data.status_code === 1) {
          toast.success("Period deleted.");
          if (editingPeriodId === periodId) {
            resetPeriodForm();
          }
          loadPeriods();
        } else toast.error(res.data.status_message || "Failed.");
      })
      .catch(() => toast.error("Network error."));
  };

  const addMapping = () => {
    if (!(parseInt(SessionId, 10) > 0 && WingId > 0 && ClassId > 0 && SectionId > 0 && mapSubjectId > 0 && mapTeacherId > 0)) {
      toast.error("Select session/wing/class/section/subject/teacher.");
      return;
    }
    axios
      .post(config.base_url + "TimeTableSetup/AddClassSubjectTeacher", {
        ...auth,
        SessionID: parseInt(SessionId, 10),
        SchoolID: 1,
        Wing_Id: parseInt(WingId, 10) || 0,
        Class_Id: parseInt(ClassId, 10) || 0,
        Section_Id: parseInt(SectionId, 10) || 0,
        Subject_ID: parseInt(mapSubjectId, 10) || 0,
        Teacher_ID: parseInt(mapTeacherId, 10) || 0,
        PeriodsPerWeek: parseInt(mapPeriods, 10) || 0,
        Remarks: mapRemarks,
      })
      .then((res) => {
        if (res.data.status_code === 1) {
          toast.success("Mapping added.");
          setMapSubjectId(0);
          setMapTeacherId(0);
          setMapPeriods(0);
          setMapRemarks("");
          loadMappings();
        } else toast.error(res.data.status_message || "Failed.");
      })
      .catch(() => toast.error("Network error."));
  };

  const startEditMapping = (mapping) => {
    setEditingMappingId(mapping.ID || 0);
    setMapSubjectId(mapping.Subject_ID || 0);
    setMapTeacherId(mapping.Teacher_ID || 0);
    setMapPeriods(mapping.PeriodsPerWeek || 0);
    setMapRemarks(mapping.Remarks || "");
  };

  const resetMappingForm = () => {
    setEditingMappingId(0);
    setMapSubjectId(0);
    setMapTeacherId(0);
    setMapPeriods(0);
    setMapRemarks("");
  };

  const updateMapping = () => {
    if (editingMappingId <= 0) {
      toast.error("Select a mapping to edit.");
      return;
    }
    if (!(parseInt(SessionId, 10) > 0 && WingId > 0 && ClassId > 0 && SectionId > 0 && mapSubjectId > 0 && mapTeacherId > 0)) {
      toast.error("Select session/wing/class/section/subject/teacher.");
      return;
    }
    axios
      .post(config.base_url + "TimeTableSetup/UpdateClassSubjectTeacher", {
        ...auth,
        ID: editingMappingId,
        SessionID: parseInt(SessionId, 10),
        SchoolID: 1,
        Wing_Id: parseInt(WingId, 10) || 0,
        Class_Id: parseInt(ClassId, 10) || 0,
        Section_Id: parseInt(SectionId, 10) || 0,
        Subject_ID: parseInt(mapSubjectId, 10) || 0,
        Teacher_ID: parseInt(mapTeacherId, 10) || 0,
        PeriodsPerWeek: parseInt(mapPeriods, 10) || 0,
        Remarks: mapRemarks,
      })
      .then((res) => {
        if (res.data.status_code === 1) {
          toast.success("Mapping updated.");
          resetMappingForm();
          loadMappings();
        } else toast.error(res.data.status_message || "Failed.");
      })
      .catch(() => toast.error("Network error."));
  };

  const deleteMapping = (mappingId) => {
    if (mappingId <= 0) return;
    if (!window.confirm("Delete this subject-teacher mapping?")) return;
    axios
      .post(config.base_url + "TimeTableSetup/DeleteClassSubjectTeacher", {
        ...auth,
        ID: mappingId,
      })
      .then((res) => {
        if (res.data.status_code === 1) {
          toast.success("Mapping deleted.");
          if (editingMappingId === mappingId) {
            resetMappingForm();
          }
          loadMappings();
        } else toast.error(res.data.status_message || "Failed.");
      })
      .catch(() => toast.error("Network error."));
  };

  const addTimeTableHeader = () => {
    if (!(parseInt(SessionId, 10) > 0 && WingId > 0 && ClassId > 0 && SectionId > 0 && ttName.trim() && ttFrom)) {
      toast.error("Complete session/wing/class/section and header fields.");
      return;
    }
    setLoading(true);
    axios
      .post(config.base_url + "TimeTable/AddTimeTable", {
        ...auth,
        Name: ttName.trim(),
        SessionID: parseInt(SessionId, 10),
        SchoolID: 1,
        Wing_Id: parseInt(WingId, 10) || 0,
        Class_Id: parseInt(ClassId, 10) || 0,
        Section_Id: parseInt(SectionId, 10) || 0,
        EffectiveFrom: ttFrom,
        EffectiveTo: ttTo || null,
        Remarks: ttRemarks,
        UserName,
      })
      .then((res) => {
        if (res.data.status_code === 1) {
          toast.success("Timetable header created.");
          const id = parseInt(res.data.ID, 10) || 0;
          loadTimeTables();
          if (id > 0) setSelectedTimeTableId(id);
        } else toast.error(res.data.status_message || "Failed.");
      })
      .catch(() => toast.error("Network error."))
      .finally(() => setLoading(false));
  };

  const loadSelectedHeaderForEdit = () => {
    if (selectedTimeTableId <= 0) {
      toast.error("Select a timetable first.");
      return;
    }
    const t = (timeTables || []).find((x) => x.ID === selectedTimeTableId);
    if (!t) {
      toast.error("Selected timetable not found.");
      return;
    }
    setTtName(t.Name || "");
    setTtFrom(String(t.EffectiveFrom || "").slice(0, 10));
    setTtTo(String(t.EffectiveTo || "").slice(0, 10));
    setTtRemarks(t.Remarks || "");
    setIsEditingHeader(true);
  };

  const updateTimeTableHeader = () => {
    if (!(selectedTimeTableId > 0 && parseInt(SessionId, 10) > 0 && WingId > 0 && ClassId > 0 && SectionId > 0 && ttName.trim() && ttFrom)) {
      toast.error("Select timetable and complete required header fields.");
      return;
    }
    setLoading(true);
    axios
      .post(config.base_url + "TimeTable/UpdateTimeTable", {
        ...auth,
        ID: selectedTimeTableId,
        Name: ttName.trim(),
        SessionID: parseInt(SessionId, 10),
        SchoolID: 1,
        Wing_Id: parseInt(WingId, 10) || 0,
        Class_Id: parseInt(ClassId, 10) || 0,
        Section_Id: parseInt(SectionId, 10) || 0,
        EffectiveFrom: ttFrom,
        EffectiveTo: ttTo || null,
        Remarks: ttRemarks,
      })
      .then((res) => {
        if (res.data.status_code === 1) {
          toast.success("Timetable header updated.");
          loadTimeTables();
        } else {
          toast.error(res.data.status_message || "Failed.");
        }
      })
      .catch(() => toast.error("Network error."))
      .finally(() => setLoading(false));
  };

  const resetHeaderForm = () => {
    setTtName("");
    setTtFrom("");
    setTtTo("");
    setTtRemarks("");
    setIsEditingHeader(false);
  };

  const onGridModeChange = (dayId, periodId, mode) => {
    const key = `${dayId}_${periodId}`;
    setGrid((prev) => ({
      ...prev,
      [key]: { mode, mapping: mode === "mapping" ? prev[key]?.mapping || "" : "" },
    }));
  };

  const onGridMappingChange = (dayId, periodId, mapping) => {
    const key = `${dayId}_${periodId}`;
    setGrid((prev) => ({ ...prev, [key]: { ...prev[key], mapping } }));
  };

  const saveGrid = () => {
    if (selectedTimeTableId <= 0) {
      toast.error("Select a timetable first.");
      return;
    }
    const details = [];
    weekDays.forEach((d) => {
      periods.forEach((p) => {
        const key = `${d.ID}_${p.ID}`;
        if (!isPeriodApplicableToDay(p, d.ID)) return;
        const cell = grid[key] || { mode: "", mapping: "" };
        if (!cell.mode) return;
        if (cell.mode === "break") {
          details.push({ Day_ID: d.ID, Period_ID: p.ID, Is_Break: true, Is_FreePeriod: false });
        } else if (cell.mode === "free") {
          details.push({ Day_ID: d.ID, Period_ID: p.ID, Is_Break: false, Is_FreePeriod: true });
        } else if (cell.mode === "mapping" && cell.mapping) {
          const split = cell.mapping.split("_");
          const subjectId = parseInt(split[0], 10) || 0;
          const teacherId = parseInt(split[1], 10) || 0;
          if (subjectId > 0 && teacherId > 0) {
            details.push({
              Day_ID: d.ID,
              Period_ID: p.ID,
              Subject_ID: subjectId,
              Teacher_ID: teacherId,
              Is_Break: false,
              Is_FreePeriod: false,
            });
          }
        }
      });
    });

    axios
      .post(config.base_url + "TimeTable/SaveTimeTableDetails", {
        ...auth,
        TimeTable_ID: selectedTimeTableId,
        TimeTable_Details: details,
      })
      .then((res) => {
        if (res.data.status_code === 1) {
          toast.success("Timetable grid saved.");
        } else {
          toast.error(res.data.status_message || "Failed.");
        }
      })
      .catch(() => toast.error("Network error."));
  };

  return (
    <>
      <TimeTableMenu />
      <ToastContainer />
      <div className="container body-content">
        <div className="page-head">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-md-8">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <NavLink to="/Dashboard" onClick={scrollToTop}>Dashboard</NavLink>
                    </li>
                    <li className="breadcrumb-item active">Time Table</li>
                  </ol>
                </nav>
                <div className="page-title">Time Table Management</div>
              </div>
            </div>
          </div>
        </div>

        <div className="page-content mt-3">
          <div className="container">
            <div className="card mb-3">
              <div className="card-body">
                <div className="row g-2">
                  <div className="col-md-3">
                    <label className="form-label">Session</label>
                    <select className="form-select" value={SessionId} onChange={(e) => setSessionId(e.target.value)}>
                      <option value="0">Select</option>
                      <SessionsDropDown selectedSession={parseInt(SessionId, 10) || 0} />
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Wing</label>
                    <select className="form-select" value={WingId} onChange={(e) => { setWingId(parseInt(e.target.value, 10) || 0); setClassId(0); setSectionId(0); }}>
                      <option value="0">Select</option>
                      <WingsDropDown selectedWingId={WingId} />
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Class</label>
                    <select className="form-select" value={ClassId} onChange={(e) => { setClassId(parseInt(e.target.value, 10) || 0); setSectionId(0); }}>
                      <option value="0">Select</option>
                      <ClassesDropDown WingID={WingId} SessionID={parseInt(SessionId, 10) || 0} selectedClassId={ClassId} />
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Section</label>
                    <select className="form-select" value={SectionId} onChange={(e) => setSectionId(parseInt(e.target.value, 10) || 0)}>
                      <option value="0">Select</option>
                      <SectionDropDown ClassID={ClassId} selectedSectionId={SectionId} />
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <Nav variant="tabs" activeKey={tab} onSelect={(k) => setTab(k || "masters")} className="mb-3">
              <Nav.Item><Nav.Link eventKey="masters">Week Days & Periods</Nav.Link></Nav.Item>
              <Nav.Item><Nav.Link eventKey="mapping">Subject Teacher Mapping</Nav.Link></Nav.Item>
              <Nav.Item><Nav.Link eventKey="timetable">Time Table Grid</Nav.Link></Nav.Item>
            </Nav>

            {tab === "masters" && (
              <div className="row g-3">
                <div className="col-md-6">
                  <div className="card"><div className="card-body">
                    <h6>{editingDayId > 0 ? "Edit Week Day" : "Add Week Day"}</h6>
                    <div className="row g-2">
                      <div className="col-md-8"><input className="form-control" placeholder="Name (e.g. Monday)" value={dayName} onChange={(e) => setDayName(e.target.value)} /></div>
                      <div className="col-md-4"><input type="number" min="1" className="form-control" placeholder="Order" value={dayOrder} onChange={(e) => setDayOrder(e.target.value)} /></div>
                      <div className="col-md-12 text-end">
                        <button className="button button-primary" onClick={editingDayId > 0 ? updateWeekDay : addWeekDay}>
                          {editingDayId > 0 ? "Update day" : "Add day"}
                        </button>
                        {editingDayId > 0 ? (
                          <button className="button button-light ms-2" onClick={resetWeekDayForm}>Cancel edit</button>
                        ) : null}
                      </div>
                    </div>
                    <hr />
                    <table className="table table-sm table-theme"><thead><tr><th>Name</th><th>Order</th><th>Actions</th></tr></thead><tbody>
                      {weekDays.map((d) => (
                        <tr key={d.ID}>
                          <td>{d.Name}</td>
                          <td>{d.DisplayOrder}</td>
                          <td>
                            <button className="button button-light me-2" onClick={() => startEditWeekDay(d)}>Edit</button>
                            <button className="button button-danger" onClick={() => deleteWeekDay(d.ID)}>Delete</button>
                          </td>
                        </tr>
                      ))}
                    </tbody></table>
                  </div></div>
                </div>
                <div className="col-md-6">
                  <div className="card"><div className="card-body">
                    <h6>{editingPeriodId > 0 ? "Edit Period" : "Add Period"}</h6>
                    <div className="row g-2">
                      <div className="col-md-6"><input className="form-control" placeholder="Name (Period 1)" value={periodName} onChange={(e) => setPeriodName(e.target.value)} /></div>
                      <div className="col-md-3"><input type="time" className="form-control" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} /></div>
                      <div className="col-md-3"><input type="time" className="form-control" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} /></div>
                      <div className="col-md-4"><input type="number" min="1" className="form-control" placeholder="Order" value={periodOrder} onChange={(e) => setPeriodOrder(e.target.value)} /></div>
                      <div className="col-md-4">
                        <select className="form-select" value={periodDayId} onChange={(e) => setPeriodDayId(parseInt(e.target.value, 10) || 0)}>
                          <option value="0">All Days (default)</option>
                          {weekDays.map((d) => <option key={d.ID} value={d.ID}>{d.Name}</option>)}
                        </select>
                      </div>
                      <div className="col-md-2"><div className="form-check mt-2"><input className="form-check-input" type="checkbox" checked={periodBreak} onChange={(e) => setPeriodBreak(e.target.checked)} id="periodBreak" /><label className="form-check-label" htmlFor="periodBreak">Break</label></div></div>
                      <div className="col-md-2 text-end">
                        <button className="button button-primary" onClick={editingPeriodId > 0 ? updatePeriod : addPeriod}>
                          {editingPeriodId > 0 ? "Update period" : "Add period"}
                        </button>
                      </div>
                      {editingPeriodId > 0 ? (
                        <div className="col-md-3 text-end">
                          <button className="button button-light" onClick={resetPeriodForm}>Cancel edit</button>
                        </div>
                      ) : null}
                    </div>
                    <hr />
                    <table className="table table-sm table-theme"><thead><tr><th>Name</th><th>Applies To</th><th>Time</th><th>Break</th><th>Actions</th></tr></thead><tbody>
                      {periods.map((p) => (
                        <tr key={p.ID}>
                          <td>{p.Name}</td>
                          <td>{p.Day_ID ? ((weekDays.find((d) => d.ID === p.Day_ID) || {}).Name || `Day #${p.Day_ID}`) : "All Days"}</td>
                          <td>{(p.StartTime || "").slice(0, 5)} - {(p.EndTime || "").slice(0, 5)}</td>
                          <td>{p.Is_Break ? "Yes" : "No"}</td>
                          <td>
                            <button className="button button-light me-2" onClick={() => startEditPeriod(p)}>Edit</button>
                            <button className="button button-danger" onClick={() => deletePeriod(p.ID)}>Delete</button>
                          </td>
                        </tr>
                      ))}
                    </tbody></table>
                  </div></div>
                </div>
              </div>
            )}

            {tab === "mapping" && (
              <div className="card"><div className="card-body">
                <div className="row g-2">
                  <div className="col-md-3">
                    <label className="form-label">Subject</label>
                    <select className="form-select" value={mapSubjectId} onChange={(e) => setMapSubjectId(parseInt(e.target.value, 10) || 0)}>
                      <option value="0">Select</option>
                      <SubjectDropDown selected={mapSubjectId} />
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Teacher</label>
                    <Select
                      className="react-select-container"
                      classNamePrefix="react-select"
                      placeholder="Search teacher..."
                      isClearable
                      options={teacherOptions}
                      value={teacherOptions.find((opt) => opt.value === mapTeacherId) || null}
                      onChange={(selected) => setMapTeacherId(selected ? selected.value : 0)}
                    />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label">Periods/week</label>
                    <input type="number" min="0" className="form-control" value={mapPeriods} onChange={(e) => setMapPeriods(e.target.value)} />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Remarks</label>
                    <input className="form-control" value={mapRemarks} onChange={(e) => setMapRemarks(e.target.value)} />
                  </div>
                  <div className="col-md-1 d-flex align-items-end">
                    <button className="button button-primary w-100" onClick={editingMappingId > 0 ? updateMapping : addMapping}>
                      {editingMappingId > 0 ? "Update" : "Add"}
                    </button>
                  </div>
                  {editingMappingId > 0 ? (
                    <div className="col-md-2 d-flex align-items-end">
                      <button className="button button-light w-100" onClick={resetMappingForm}>Cancel Edit</button>
                    </div>
                  ) : null}
                  <div className="col-md-12">
                    {editingMappingId > 0 ? <small className="text-muted">Editing mapping ID: {editingMappingId}</small> : null}
                  </div>
                </div>
                <hr />
                <table className="table table-sm table-theme">
                  <thead><tr><th>Sr. No</th><th>Subject</th><th>Teacher</th><th>Periods/week</th><th>Remarks</th><th>Actions</th></tr></thead>
                  <tbody>
                    {mappings.map((m, index) => (
                      <tr key={m.ID}>
                        <td>{index + 1}</td>
                        <td>{m.Subject_Name || `Subject #${m.Subject_ID}`}</td>
                        <td>{m.Teacher_Name || `Teacher #${m.Teacher_ID}`}</td>
                        <td>{m.PeriodsPerWeek}</td>
                        <td>{m.Remarks}</td>
                        <td>
                          <button className="button button-light me-2" onClick={() => startEditMapping(m)}>Edit</button>
                          <button className="button button-danger" onClick={() => deleteMapping(m.ID)}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div></div>
            )}

            {tab === "timetable" && (
              <div className="card"><div className="card-body">
                <div className="row g-2 mb-3">
                  <div className="col-md-3"><input className="form-control" placeholder="Timetable name" value={ttName} onChange={(e) => setTtName(e.target.value)} /></div>
                  <div className="col-md-2"><input type="date" className="form-control" value={ttFrom} onChange={(e) => setTtFrom(e.target.value)} /></div>
                  <div className="col-md-2"><input type="date" className="form-control" value={ttTo} onChange={(e) => setTtTo(e.target.value)} /></div>
                  <div className="col-md-3"><input className="form-control" placeholder="Remarks" value={ttRemarks} onChange={(e) => setTtRemarks(e.target.value)} /></div>
                  <div className="col-md-2 text-end">
                    <button className="button button-primary w-100" onClick={isEditingHeader ? updateTimeTableHeader : addTimeTableHeader}>
                      {isEditingHeader ? "Update header" : "Create header"}
                    </button>
                  </div>
                </div>

                <div className="row g-2 mb-3">
                  <div className="col-md-6">
                    <label className="form-label">Select timetable</label>
                    <select className="form-select" value={selectedTimeTableId} onChange={(e) => setSelectedTimeTableId(parseInt(e.target.value, 10) || 0)}>
                      <option value="0">Select</option>
                      {timeTables.map((t) => (
                        <option key={t.ID} value={t.ID}>
                          {t.Name} ({String(t.EffectiveFrom || "").slice(0, 10)})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-3 d-flex align-items-end">
                    <button className="button button-secondary w-100" onClick={loadSelectedHeaderForEdit}>Edit selected header</button>
                  </div>
                  <div className="col-md-3 d-flex align-items-end">
                    <button className="button button-light w-100" onClick={resetHeaderForm}>Clear header form</button>
                  </div>
                </div>

                <div className="table-responsive">
                  <table className="table table-sm table-theme">
                    <thead>
                      <tr>
                        <th style={{ minWidth: 160 }}>Period</th>
                        {weekDays.map((d) => <th key={d.ID} style={{ minWidth: 220 }}>{d.Name}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {periods.map((p) => (
                        <tr key={p.ID}>
                          <td>
                            <div>{p.Name}</div>
                            <small className="text-muted">{(p.StartTime || "").slice(0, 5)} - {(p.EndTime || "").slice(0, 5)}</small>
                          </td>
                          {weekDays.map((d) => {
                            const key = `${d.ID}_${p.ID}`;
                            const cell = grid[key] || { mode: "", mapping: "" };
                            const applicable = isPeriodApplicableToDay(p, d.ID);
                            return (
                              <td key={key}>
                                {!applicable ? (
                                  <span className="text-muted">-</span>
                                ) : (
                                  <>
                                    <select
                                      className="form-select form-select-sm mb-1"
                                      value={cell.mode}
                                      onChange={(e) => onGridModeChange(d.ID, p.ID, e.target.value)}
                                    >
                                      <option value="">Empty</option>
                                      <option value="mapping">Subject + Teacher</option>
                                      <option value="break">Break</option>
                                      <option value="free">Free Period</option>
                                    </select>
                                    {cell.mode === "mapping" ? (
                                      <select
                                        className="form-select form-select-sm"
                                        value={cell.mapping}
                                        onChange={(e) => onGridMappingChange(d.ID, p.ID, e.target.value)}
                                      >
                                        <option value="">Select mapping</option>
                                        {mappings.map((m) => (
                                          <option key={m.ID} value={mappingKey(m.Subject_ID, m.Teacher_ID)}>
                                            {(m.Subject_Name || `S#${m.Subject_ID}`)} - {(m.Teacher_Name || `T#${m.Teacher_ID}`)}
                                          </option>
                                        ))}
                                      </select>
                                    ) : null}
                                  </>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="text-end">
                  <button className="button button-primary" onClick={saveGrid}>Save grid</button>
                </div>
              </div></div>
            )}
          </div>
        </div>
      </div>
      {loading ? <div className="text-center my-3"><Spinner animation="border" /></div> : null}
    </>
  );
}

export default TimeTableManagement;
